import React, { useEffect, useMemo, useRef, useState } from 'react';
import PePapersDisclaimerGate from './shared/PePapersDisclaimerGate';
import PePapersStorageHint, { PE_PAPERS_STORAGE_HINTS } from './PePapersStorageHint';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { savePePapersArchiveDoc } from '../../services/pePapersArchiveService';
import useAppStore from '../../store/appStore';
import { usePersonalDataSelector } from '../../store/usePersonalDataSelector';
import { buildHtmlAsPdfBlob, downloadHtmlAsPdf } from '../../utils/pe-papers/htmlDocumentPdfDownload';

export interface PeSachbezugData {
    arbeitgeberName: string;
    arbeitgeberStrasse: string;
    arbeitgeberPlzOrt: string;
    mitarbeiterName: string;
    mitarbeiterStrasse: string;
    mitarbeiterPlzOrt: string;
    sachbezugArt: SachbezugArt;
    sachbezugDetails: string;
    monatlicherWert: number;
    ausgabeBeginn: string;
    ausgabeIntervall: 'monatlich';
    ausgabeTag: number;
    erstellungsDatum: string;
}

export type SachbezugArt = 'Guthabenkarte' | 'Tankgutschein' | 'Regionalgutschein' | 'Individuell';

export interface PeSachbezugModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: any;
    onArchived?: () => void;
}

interface BetriebFormData {
    firmenname: string;
    strasse: string;
    plz: string;
    ort: string;
}

interface MitarbeiterFormData {
    name: string;
    strasse: string;
    plz: string;
    ort: string;
}

interface BetriebFieldLocks {
    firmenname: boolean;
    strasse: boolean;
    plz: boolean;
    ort: boolean;
}

type TeamPool = 'produktiv' | 'unproduktiv' | 'azubi';

type TeamEmployeeOption = {
    id: string;
    name: string;
    pool: TeamPool;
    adresse: string;
};

const SB_CONTRACT_PDF_CSS = `
  .av-a4-page {
    display: block !important;
    width: 100% !important;
    max-width: 210mm !important;
    margin-left: auto !important;
    margin-right: auto !important;
    box-sizing: border-box !important;
    font-family: Georgia, 'Times New Roman', serif !important;
    color: #0f172a !important;
    font-size: 12px !important;
    line-height: 1.45 !important;
    padding: 10mm 13mm 14mm 13mm !important;
    background: #ffffff !important;
  }
  .av-a4-page * { box-sizing: border-box !important; }
  .av-a4-page .pdf-letterhead {
    width: 100% !important;
    text-align: center !important;
    border-bottom: 2px solid #0f172a !important;
    margin: 0 0 11px !important;
    padding: 0 0 8px !important;
  }
  .av-a4-page .pdf-letterhead h1 {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 15px !important;
    font-weight: 900 !important;
    letter-spacing: 0.06em !important;
    text-transform: uppercase !important;
    color: #0f172a !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 0 4px !important;
    text-align: center !important;
  }
  .av-a4-page .pdf-letterhead h2 {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12px !important;
    font-weight: 700 !important;
    letter-spacing: 0.14em !important;
    text-transform: uppercase !important;
    color: #b48a56 !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
    text-align: center !important;
  }
  .av-meta-grid {
    display: table !important;
    width: 100% !important;
    table-layout: fixed !important;
    margin: 0 0 11px !important;
    border: 1px solid #cbd5e1 !important;
    background: #f8fafc !important;
  }
  .av-meta-col {
    display: table-cell !important;
    width: 50% !important;
    vertical-align: top !important;
    padding: 9px 11px !important;
    font-size: 11px !important;
    line-height: 1.4 !important;
    color: #334155 !important;
    text-align: left !important;
  }
  .av-meta-col + .av-meta-col { border-left: 1px solid #e2e8f0 !important; }
  .av-meta-label {
    display: block !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11.5px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.12em !important;
    color: #64748b !important;
    margin: 0 0 4px !important;
  }
  .av-meta-col strong { color: #0f172a !important; font-weight: 700 !important; }
  .av-meta-col p {
    width: 100% !important;
    margin: 0 !important;
    text-align: left !important;
    font-size: 11px !important;
    line-height: 1.4 !important;
    color: #334155 !important;
  }
  .av-doc-section {
    display: block !important;
    width: 100% !important;
    margin: 0 0 9px !important;
  }
  .av-a4-page h3,
  .av-doc-section > h3 {
    display: block !important;
    width: 100% !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 12.5px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.05em !important;
    text-align: left !important;
    background: #0f172a !important;
    color: #f8fafc !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 5px 9px !important;
    margin: 0 0 5px !important;
  }
  .av-a4-page p,
  .av-doc-p,
  .av-doc-section p {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    font-family: Georgia, 'Times New Roman', serif !important;
    font-size: 12px !important;
    line-height: 1.45 !important;
    color: #1e293b !important;
    text-align: justify !important;
    margin: 0 0 5px !important;
  }
  .ab-sig-table {
    width: 100% !important;
    border-collapse: collapse !important;
    table-layout: fixed !important;
    margin-top: 16px !important;
    border: none !important;
  }
  .ab-sig-table td {
    width: 50% !important;
    vertical-align: top !important;
    border: none !important;
    background: transparent !important;
    padding: 0 8px 0 0 !important;
  }
  .ab-sig-table td + td { padding: 0 0 0 8px !important; }
  .ab-sig-table p {
    text-align: left !important;
    margin: 0 !important;
    width: auto !important;
    max-width: 100% !important;
    font-family: Arial, Helvetica, sans-serif !important;
  }
  @media print {
    @page { size: A4; margin: 0; }
    body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
    .av-a4-page {
      box-shadow: none !important;
      margin: 0 !important;
      max-width: none !important;
      width: 100% !important;
      min-height: 0 !important;
    }
  }
`;

const SACHBEZUG_ART_OPTIONS: SachbezugArt[] = [
    'Guthabenkarte',
    'Tankgutschein',
    'Regionalgutschein',
    'Individuell',
];

const INITIAL_MITARBEITER_DATA: MitarbeiterFormData = {
    name: '',
    strasse: '',
    plz: '',
    ort: '',
};

const A4_PREVIEW_PAGE_CLASS =
    'av-a4-page bg-white text-slate-900 my-4 mx-auto w-full max-w-[210mm] h-auto min-h-0 shadow-xl rounded-sm text-left block overflow-visible print:my-0 print:shadow-none';

const DOC_P_CLASS = 'av-doc-p mb-2 text-justify text-slate-800 leading-relaxed';

const INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black text-pe-text dark:text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E6C299]/50 transition-colors';

const DATE_INPUT_CLASS = `${INPUT_CLASS} cursor-pointer kombi-date-input`;
const LABEL_CLASS = 'text-xs font-semibold text-pe-muted dark:text-slate-400 uppercase tracking-wider mb-1 block';
const READONLY_INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] text-pe-accent-text dark:text-[#E6C299] px-4 py-2.5 text-sm cursor-not-allowed opacity-90';
const ERROR_INPUT_RING = 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500/20';

const BETRIEB_AUTOCOMPLETE: Record<keyof BetriebFormData, string> = {
    firmenname: 'organization',
    strasse: 'street-address',
    plz: 'postal-code',
    ort: 'address-level2',
};

const STEP_LABELS: Record<number, string> = {
    1: 'Konfiguration',
    2: 'Vorschau & Archiv',
};

const isEmpty = (value: string) => !String(value || '').trim();

const todayIso = () => new Date().toISOString().slice(0, 10);

const formatDisplayDate = (value: string): string => {
    if (!value.trim()) return '____________';
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) return `${isoMatch[3]}.${isoMatch[2]}.${isoMatch[1]}`;
    return value.trim();
};

const formatEuroDe = (value: number): string =>
    value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const parseLegacyMitarbeiterAdresse = (
    adresse: string
): Pick<MitarbeiterFormData, 'strasse' | 'plz' | 'ort'> => {
    const raw = String(adresse || '').trim();
    if (!raw) return { strasse: '', plz: '', ort: '' };

    const lines = raw
        .split(/[\n;]+/)
        .map((s) => s.trim())
        .filter(Boolean);

    const plzOrtMatch = (value: string) => {
        const m = value.match(/^(\d{4,5})\s+(.+)$/);
        return m ? { plz: m[1], ort: m[2].trim() } : null;
    };

    if (lines.length === 1) {
        const parts = lines[0]
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        if (parts.length >= 2) {
            const last = plzOrtMatch(parts[parts.length - 1]);
            if (last) {
                return {
                    strasse: parts.slice(0, -1).join(', '),
                    plz: last.plz,
                    ort: last.ort,
                };
            }
        }
        const single = plzOrtMatch(lines[0]);
        if (single) return { strasse: '', ...single };
        return { strasse: lines[0], plz: '', ort: '' };
    }

    const lastLine = plzOrtMatch(lines[lines.length - 1]);
    if (lastLine) {
        return {
            strasse: lines.slice(0, -1).join(', '),
            plz: lastLine.plz,
            ort: lastLine.ort,
        };
    }
    return { strasse: lines[0], plz: '', ort: lines.slice(1).join(' ') };
};

const normalizeMitarbeiterData = (incoming: any): MitarbeiterFormData => {
    const src = incoming && typeof incoming === 'object' ? incoming : {};
    const hasStructured =
        !isEmpty(String(src.strasse || '')) ||
        !isEmpty(String(src.plz || '')) ||
        !isEmpty(String(src.ort || ''));
    if (!hasStructured && typeof src.adresse === 'string' && src.adresse.trim()) {
        const parsed = parseLegacyMitarbeiterAdresse(src.adresse);
        return {
            name: String(src.name || ''),
            strasse: parsed.strasse,
            plz: parsed.plz,
            ort: parsed.ort,
        };
    }
    return {
        name: String(src.name || ''),
        strasse: String(src.strasse || ''),
        plz: String(src.plz || ''),
        ort: String(src.ort || ''),
    };
};

const buildBetriebFromClient = (clientData: any): { betrieb: BetriebFormData; locks: BetriebFieldLocks } => {
    const firmenname = clientData?.salonname || clientData?.firmenname || '';
    const strasse = clientData?.strasse || '';
    const plz = clientData?.plz || '';
    const ort = clientData?.ort || '';
    return {
        betrieb: { firmenname, strasse, plz, ort },
        locks: {
            firmenname: !isEmpty(firmenname),
            strasse: !isEmpty(strasse),
            plz: !isEmpty(plz),
            ort: !isEmpty(ort),
        },
    };
};

const clampWert = (raw: number) => {
    if (!Number.isFinite(raw) || raw < 0) return 0;
    return Math.min(50, Math.round(raw * 100) / 100);
};

const PeSachbezugModal: React.FC<PeSachbezugModalProps> = ({ isOpen, onClose, initialData, onArchived }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [betriebData, setBetriebData] = useState<BetriebFormData>({
        firmenname: '',
        strasse: '',
        plz: '',
        ort: '',
    });
    const [betriebLocks, setBetriebLocks] = useState<BetriebFieldLocks>({
        firmenname: false,
        strasse: false,
        plz: false,
        ort: false,
    });
    const [mitarbeiterData, setMitarbeiterData] = useState<MitarbeiterFormData>(INITIAL_MITARBEITER_DATA);
    const [selectedTeamEmployeeId, setSelectedTeamEmployeeId] = useState('');
    const [sachbezugArt, setSachbezugArt] = useState<SachbezugArt>('Guthabenkarte');
    const [sachbezugDetails, setSachbezugDetails] = useState('');
    const [monatlicherWert, setMonatlicherWert] = useState('50');
    const [ausgabeBeginn, setAusgabeBeginn] = useState(todayIso);
    const [ausgabeTag, setAusgabeTag] = useState('1');
    const [erstellungsDatum, setErstellungsDatum] = useState(todayIso);
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isPdfDownloading, setIsPdfDownloading] = useState(false);
    const [archiveFeedback, setArchiveFeedback] = useState<'idle' | 'success' | 'error' | 'draft'>('idle');
    const [archiveDocId, setArchiveDocId] = useState<string | null>(null);

    const printContainerRef = useRef<HTMLDivElement>(null);
    const clientData = useAppStore((state: any) => state.clientData);
    const { personalData, unproductivePersonalData, azubiAssistenzData } = usePersonalDataSelector();

    const teamEmployees = useMemo((): TeamEmployeeOption[] => {
        const mapPool = (list: any[], pool: TeamPool): TeamEmployeeOption[] =>
            (Array.isArray(list) ? list : [])
                .filter((emp) => emp?.id)
                .map((emp) => ({
                    id: String(emp.id),
                    name: String(emp.name || '').trim() || 'Ohne Name',
                    pool,
                    adresse: String(emp.adresse || emp.info || '').trim(),
                }));
        return [
            ...mapPool(personalData, 'produktiv'),
            ...mapPool(unproductivePersonalData, 'unproduktiv'),
            ...mapPool(azubiAssistenzData, 'azubi'),
        ];
    }, [personalData, unproductivePersonalData, azubiAssistenzData]);

    const resetForm = () => {
        setCurrentStep(1);
        setMitarbeiterData(INITIAL_MITARBEITER_DATA);
        setSelectedTeamEmployeeId('');
        setSachbezugArt('Guthabenkarte');
        setSachbezugDetails('');
        setMonatlicherWert('50');
        setAusgabeBeginn(todayIso());
        setAusgabeTag('1');
        setErstellungsDatum(todayIso());
        setInvalidFields(new Set());
        setDisclaimerAccepted(false);
        setIsSaving(false);
        setArchiveFeedback('idle');
        setArchiveDocId(null);
    };

    useEffect(() => {
        if (!isOpen) {
            resetForm();
            return;
        }
        const { betrieb, locks } = buildBetriebFromClient(clientData);
        resetForm();
        setBetriebData(betrieb);
        setBetriebLocks(locks);
        setArchiveDocId(initialData?.id || null);

        if (initialData) {
            if (initialData.betriebData && typeof initialData.betriebData === 'object') {
                setBetriebData({ ...betrieb, ...initialData.betriebData });
                setBetriebLocks({
                    firmenname: !isEmpty(String(initialData.betriebData.firmenname ?? betrieb.firmenname)),
                    strasse: !isEmpty(String(initialData.betriebData.strasse ?? betrieb.strasse)),
                    plz: !isEmpty(String(initialData.betriebData.plz ?? betrieb.plz)),
                    ort: !isEmpty(String(initialData.betriebData.ort ?? betrieb.ort)),
                });
            }
            if (initialData.mitarbeiterData && typeof initialData.mitarbeiterData === 'object') {
                setMitarbeiterData(normalizeMitarbeiterData(initialData.mitarbeiterData));
            }
            if (typeof initialData.selectedTeamEmployeeId === 'string') {
                setSelectedTeamEmployeeId(initialData.selectedTeamEmployeeId);
            }
            if (SACHBEZUG_ART_OPTIONS.includes(initialData.sachbezugArt)) {
                setSachbezugArt(initialData.sachbezugArt);
            }
            if (typeof initialData.sachbezugDetails === 'string') setSachbezugDetails(initialData.sachbezugDetails);
            if (initialData.monatlicherWert != null && initialData.monatlicherWert !== '') {
                setMonatlicherWert(String(clampWert(Number(initialData.monatlicherWert))));
            }
            if (typeof initialData.ausgabeBeginn === 'string') setAusgabeBeginn(initialData.ausgabeBeginn);
            if (initialData.ausgabeTag != null && initialData.ausgabeTag !== '') {
                setAusgabeTag(String(initialData.ausgabeTag));
            }
            if (typeof initialData.erstellungsDatum === 'string') setErstellungsDatum(initialData.erstellungsDatum);
            const step = Number(initialData.currentStep);
            if (Number.isFinite(step) && step >= 1 && step <= 2) setCurrentStep(step);
            if (initialData.confirmedDisclaimer) setDisclaimerAccepted(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, clientData, initialData]);

    if (!isOpen) return null;

    const clearFieldError = (field: string) => {
        setInvalidFields((prev) => {
            if (!prev.has(field)) return prev;
            const next = new Set(prev);
            next.delete(field);
            return next;
        });
    };

    const isFieldInvalid = (field: string) => invalidFields.has(field);
    const getInputClass = (baseClass: string, field: string) =>
        isFieldInvalid(field) ? `${baseClass} ${ERROR_INPUT_RING}` : baseClass;

    const updateBetriebField = <K extends keyof BetriebFormData>(key: K, value: BetriebFormData[K]) => {
        setBetriebData((prev) => ({ ...prev, [key]: value }));
        clearFieldError(`betrieb_${key}`);
    };

    const updateMitarbeiterField = <K extends keyof MitarbeiterFormData>(key: K, value: MitarbeiterFormData[K]) => {
        setMitarbeiterData((prev) => ({ ...prev, [key]: value }));
        clearFieldError(`mitarbeiter_${key}`);
    };

    const handleTeamEmployeeSelect = (employeeId: string) => {
        setSelectedTeamEmployeeId(employeeId);
        if (!employeeId) return;
        const emp = teamEmployees.find((e) => e.id === employeeId);
        if (!emp) return;
        const parsedAdresse = parseLegacyMitarbeiterAdresse(emp.adresse);
        setMitarbeiterData({
            name: emp.name === 'Ohne Name' ? '' : emp.name,
            strasse: parsedAdresse.strasse,
            plz: parsedAdresse.plz,
            ort: parsedAdresse.ort,
        });
        clearFieldError('mitarbeiter_name');
        clearFieldError('mitarbeiter_strasse');
        clearFieldError('mitarbeiter_plz');
        clearFieldError('mitarbeiter_ort');
    };

    const validateStep1 = (): string[] => {
        const errors: string[] = [];
        if (isEmpty(betriebData.firmenname)) errors.push('betrieb_firmenname');
        if (isEmpty(betriebData.strasse)) errors.push('betrieb_strasse');
        if (isEmpty(betriebData.plz)) errors.push('betrieb_plz');
        if (isEmpty(betriebData.ort)) errors.push('betrieb_ort');
        if (isEmpty(mitarbeiterData.name)) errors.push('mitarbeiter_name');
        if (isEmpty(mitarbeiterData.strasse)) errors.push('mitarbeiter_strasse');
        if (isEmpty(mitarbeiterData.plz)) errors.push('mitarbeiter_plz');
        if (isEmpty(mitarbeiterData.ort)) errors.push('mitarbeiter_ort');
        if (isEmpty(sachbezugDetails)) errors.push('sachbezug_details');
        if (isEmpty(ausgabeBeginn)) errors.push('ausgabe_beginn');
        if (isEmpty(erstellungsDatum)) errors.push('erstellungs_datum');
        const wert = Number(String(monatlicherWert).replace(',', '.'));
        if (!Number.isFinite(wert) || wert <= 0 || wert > 50) errors.push('monatlicher_wert');
        const tag = Number(ausgabeTag);
        if (!Number.isFinite(tag) || tag < 1 || tag > 28) errors.push('ausgabe_tag');
        return errors;
    };

    const handleWeiter = () => {
        const errors = validateStep1();
        if (errors.length > 0) {
            setInvalidFields(new Set(errors));
            return;
        }
        setInvalidFields(new Set());
        setCurrentStep(2);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const isActionLocked = !disclaimerAccepted;

    const getActionButtonClass = (baseClass: string) =>
        `${baseClass} ${isActionLocked ? 'opacity-50 cursor-not-allowed' : ''}`;

    const wertNum = clampWert(Number(String(monatlicherWert).replace(',', '.')));
    const tagNum = Math.min(28, Math.max(1, Math.floor(Number(ausgabeTag) || 1)));
    const ortDatum = `${betriebData.ort || '____________'}, ${formatDisplayDate(erstellungsDatum)}`;

    const documentData: PeSachbezugData = {
        arbeitgeberName: betriebData.firmenname,
        arbeitgeberStrasse: betriebData.strasse,
        arbeitgeberPlzOrt: [betriebData.plz, betriebData.ort].filter(Boolean).join(' '),
        mitarbeiterName: mitarbeiterData.name,
        mitarbeiterStrasse: mitarbeiterData.strasse,
        mitarbeiterPlzOrt: [mitarbeiterData.plz, mitarbeiterData.ort].filter(Boolean).join(' '),
        sachbezugArt,
        sachbezugDetails: sachbezugDetails.trim(),
        monatlicherWert: wertNum,
        ausgabeBeginn: formatDisplayDate(ausgabeBeginn),
        ausgabeIntervall: 'monatlich',
        ausgabeTag: tagNum,
        erstellungsDatum: formatDisplayDate(erstellungsDatum),
    };

    const getDocumentHtml = () =>
        printContainerRef.current?.innerHTML ||
        document.getElementById('pepapers-sachbezug-print')?.innerHTML ||
        '';

    const buildPdfOptions = (html: string) => {
        const dateIso = new Date().toISOString().slice(0, 10);
        return {
            html,
            filename: `Sachbezug_50_EUR_${dateIso}.pdf`,
            scrubMode: 'minimal' as const,
            splitPrintBreaks: true as const,
            capturePadPx: 0,
            letterheadOnBreaks: false,
            extraCss: SB_CONTRACT_PDF_CSS,
        };
    };

    const handlePrintIsolated = async () => {
        if (isActionLocked || isPdfDownloading) return;
        const html = getDocumentHtml();
        if (!html.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }
        setIsPdfDownloading(true);
        try {
            const blob = await buildHtmlAsPdfBlob(buildPdfOptions(html));
            const url = URL.createObjectURL(blob);
            const iframe = document.createElement('iframe');
            iframe.setAttribute('title', 'Sachbezug Druck');
            iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
            document.body.appendChild(iframe);
            const cleanup = () => {
                window.setTimeout(() => {
                    iframe.remove();
                    URL.revokeObjectURL(url);
                }, 1500);
            };
            iframe.onload = () => {
                try {
                    iframe.contentWindow?.focus();
                    iframe.contentWindow?.print();
                } finally {
                    cleanup();
                }
            };
            iframe.src = url;
        } catch (err) {
            console.error('PEPapers Sachbezug Druck fehlgeschlagen:', err);
            window.alert('Druckvorschau konnte nicht erstellt werden.');
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (isActionLocked || isPdfDownloading) return;
        const html = getDocumentHtml();
        if (!html.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }
        setIsPdfDownloading(true);
        try {
            await downloadHtmlAsPdf(buildPdfOptions(html));
        } catch (err: any) {
            console.error('PEPapers Sachbezug PDF-Download fehlgeschlagen:', err);
            window.alert(
                'PDF konnte nicht heruntergeladen werden: ' +
                    (String(err?.message || '').match(/setEnd|Range|offset/i)
                        ? 'PDF-Erstellung ist fehlgeschlagen. Bitte Seite neu laden und erneut versuchen.'
                        : err?.message || 'Unbekannter Fehler')
            );
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handleArchive = async (asDraft = false) => {
        if (isSaving) return;
        if (!asDraft && isActionLocked) return;
        setIsSaving(true);
        setArchiveFeedback('idle');
        try {
            const id = await savePePapersArchiveDoc({
                id: archiveDocId,
                documentType: 'sachbezug-50',
                status: asDraft ? 'entwurf' : 'final',
                salonName: betriebData.firmenname || 'Unbekannter Salon',
                mitarbeiterName: mitarbeiterData.name.trim(),
                confirmedDisclaimer: asDraft ? disclaimerAccepted : true,
                currentStep,
                payload: {
                    betriebData,
                    mitarbeiterData,
                    selectedTeamEmployeeId: selectedTeamEmployeeId || null,
                    sachbezugArt,
                    sachbezugDetails,
                    monatlicherWert: wertNum,
                    ausgabeBeginn,
                    ausgabeTag: tagNum,
                    erstellungsDatum,
                    documentData,
                    generatedHtml: currentStep === 2 ? getDocumentHtml() : '',
                },
            });
            setArchiveDocId(id);
            setArchiveFeedback(asDraft ? 'draft' : 'success');
            onArchived?.();
            if (asDraft) {
                setTimeout(() => onClose(), 600);
            } else {
                setTimeout(() => setArchiveFeedback('idle'), 3000);
            }
        } catch (error) {
            console.error('PEPapers Sachbezug Archivierung fehlgeschlagen:', error);
            setArchiveFeedback('error');
        } finally {
            setIsSaving(false);
        }
    };

    const renderFieldError = (field: string) =>
        isFieldInvalid(field) ? <p className="text-[10px] text-red-400 mt-1">Dieses Feld ist erforderlich</p> : null;

    const renderBetriebField = (field: keyof BetriebFormData, label: string) => {
        const isLocked = betriebLocks[field];
        const fieldId = `betrieb_${field}`;
        return (
            <div>
                <label className={LABEL_CLASS}>{label}</label>
                <input
                    type="text"
                    value={betriebData[field]}
                    readOnly={isLocked}
                    autoComplete={isLocked ? 'off' : BETRIEB_AUTOCOMPLETE[field]}
                    onChange={(e) => !isLocked && updateBetriebField(field, e.target.value)}
                    className={getInputClass(isLocked ? READONLY_INPUT_CLASS : INPUT_CLASS, fieldId)}
                />
                {renderFieldError(fieldId)}
            </div>
        );
    };

    const renderContractDocument = () => (
        <div ref={printContainerRef} id="pepapers-sachbezug-print" className="w-full h-auto overflow-visible">
            <style>{SB_CONTRACT_PDF_CSS}</style>
            <div className={`${A4_PREVIEW_PAGE_CLASS} min-h-[297mm]`}>
                <div className="pdf-letterhead text-center">
                    <h1>Zusatzvereinbarung zum Arbeitsvertrag</h1>
                    <h2>PreisExpert · PEPapers · Steuerfreier Sachbezug (§ 8 Abs. 2 EStG)</h2>
                </div>

                <div className="av-meta-grid">
                    <div className="av-meta-col">
                        <p className="av-meta-label">Arbeitgeber</p>
                        <p>
                            <strong>{documentData.arbeitgeberName || '____________'}</strong>
                            <br />
                            {documentData.arbeitgeberStrasse || '____________'}
                            <br />
                            {documentData.arbeitgeberPlzOrt || '____________'}
                        </p>
                    </div>
                    <div className="av-meta-col">
                        <p className="av-meta-label">Arbeitnehmer</p>
                        <p>
                            <strong>{documentData.mitarbeiterName || '____________'}</strong>
                            <br />
                            {documentData.mitarbeiterStrasse || '____________'}
                            <br />
                            {documentData.mitarbeiterPlzOrt || '____________'}
                        </p>
                    </div>
                </div>

                <p className={DOC_P_CLASS}>
                    Zwischen dem Arbeitgeber und dem Arbeitnehmer wird folgende Zusatzvereinbarung geschlossen:
                </p>

                <div className="av-doc-section">
                    <h3>§ 1 Gegenstand der Vereinbarung &amp; Freigrenze</h3>
                    <p className={DOC_P_CLASS}>
                        (1) Der Arbeitgeber gewährt dem Arbeitnehmer ab dem {documentData.ausgabeBeginn} einen
                        monatlichen Sachbezug in Form von: {documentData.sachbezugArt} (
                        {documentData.sachbezugDetails || '____________'}).
                    </p>
                    <p className={DOC_P_CLASS}>
                        (2) Der Nennwert beträgt monatlich {formatEuroDe(documentData.monatlicherWert)} EUR. Die
                        Ausgabe erfolgt monatlich jeweils am {documentData.ausgabeTag}. des Monats.
                    </p>
                    <p className={DOC_P_CLASS}>
                        (3) Es handelt sich um eine Sachzuwendung im Rahmen der gesetzlichen Freigrenze von 50,00 EUR
                        monatlich gemäß § 8 Abs. 2 Satz 11 EStG.
                    </p>
                </div>

                <div className="av-doc-section">
                    <h3>§ 2 Zusätzlichkeitserfordernis (§ 8 Abs. 4 EStG)</h3>
                    <p className={DOC_P_CLASS}>
                        (1) Die Leistung wird ausschließlich und uneingeschränkt zusätzlich zu dem ohnehin geschuldeten
                        Arbeitslohn gewährt.
                    </p>
                    <p className={DOC_P_CLASS}>
                        (2) Eine Umwandlung bestehender Vergütungsbestandteile (Barlohn, Überstundenvergütung,
                        Provisionen oder tarifliche Grundlöhne) ist ausgeschlossen.
                    </p>
                </div>

                <div className="av-doc-section">
                    <h3>§ 3 Ausschluss von Barauszahlungen &amp; Zweckbindung (ZAG)</h3>
                    <p className={DOC_P_CLASS}>
                        (1) Eine Barauszahlung des Sachbezugs, die Umwandlung in Barlohn oder eine Überweisung auf ein
                        Girokonto ist zwingend ausgeschlossen.
                    </p>
                    <p className={DOC_P_CLASS}>
                        (2) Das eingesetzte Zahlungsinstrument erfüllt die Kriterien des § 2 Abs. 1 Nr. 10
                        Zahlungsdiensteaufsichtsgesetzes (ZAG) und berechtigt ausschließlich zum Bezug von Waren oder
                        Dienstleistungen im festgelegten Akzeptanzstellennetz.
                    </p>
                </div>

                <div className="av-doc-section">
                    <h3>§ 4 Freiwilligkeit &amp; Widerrufsvorbehalt</h3>
                    <p className={DOC_P_CLASS}>
                        (1) Die Gewährung des Sachbezugs ist eine freiwillige Leistung des Arbeitgebers. Auch bei
                        wiederholter, mehrfacher oder regelmäßiger Gewährung entsteht kein Rechtsanspruch für die
                        Zukunft (Ausschluss einer betrieblichen Übung).
                    </p>
                    <p className={DOC_P_CLASS}>
                        (2) Der Arbeitgeber behält sich das Recht vor, die Gewährung des Sachbezugs mit einer Frist von
                        einem Monat zum Monatsende ganz oder teilweise zu widerrufen, insbesondere bei wirtschaftlichen
                        Schwierigkeiten des Betriebs, Änderungen der steuerrechtlichen Rahmenbedingungen oder nach
                        Ausspruch einer Kündigung des Arbeitsverhältnisses.
                    </p>
                    <p className={DOC_P_CLASS}>
                        (3) In Zeiten, in denen kein Entgeltfortzahlungsanspruch besteht (z. B. Elternzeit, unbezahlter
                        Urlaub, Bezug von Krankengeld nach Ablauf von 6 Wochen), ruht der Anspruch auf den Sachbezug.
                    </p>
                </div>

                <table className="ab-sig-table">
                    <tbody>
                        <tr>
                            <td>
                                <p style={{ margin: 0, fontSize: '11px', color: '#475569' }}>
                                    Ort, Datum: {ortDatum}
                                </p>
                                <div style={{ height: '36px' }} aria-hidden="true" />
                                <div
                                    style={{
                                        width: '200px',
                                        maxWidth: '100%',
                                        borderBottom: '2px solid #1e293b',
                                        marginBottom: '6px',
                                    }}
                                />
                                <p style={{ margin: 0, fontSize: '10px', color: '#64748b' }}>Unterschrift Arbeitgeber</p>
                                <p style={{ margin: '2px 0 0', fontSize: '11.5px', fontWeight: 700, color: '#0f172a' }}>
                                    {documentData.arbeitgeberName || 'Salon / Arbeitgeber'}
                                </p>
                            </td>
                            <td>
                                <p style={{ margin: 0, fontSize: '11px', color: '#475569' }}>
                                    Ort, Datum: ________________________
                                </p>
                                <div style={{ height: '36px' }} aria-hidden="true" />
                                <div
                                    style={{
                                        width: '200px',
                                        maxWidth: '100%',
                                        borderBottom: '2px solid #1e293b',
                                        marginBottom: '6px',
                                    }}
                                />
                                <p style={{ margin: 0, fontSize: '10px', color: '#64748b' }}>
                                    Unterschrift Arbeitnehmer
                                </p>
                                <p style={{ margin: '2px 0 0', fontSize: '11.5px', fontWeight: 700, color: '#0f172a' }}>
                                    {documentData.mitarbeiterName || 'Name des Mitarbeiters'}
                                </p>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );

    const modalWidthClass = currentStep === 2 ? 'max-w-4xl' : 'max-w-lg';

    return createPortal(
        <>
            <style>{`input.kombi-date-input[type='date']::-webkit-calendar-picker-indicator{filter:invert(1);cursor:pointer}`}</style>
            <div
                className="fixed inset-0 bg-stone-900/70 dark:bg-black/90 z-[100000] backdrop-blur-md print:hidden"
                onClick={handleClose}
                aria-hidden="true"
            />
            <div className="fixed inset-x-0 top-[8.5rem] md:top-36 bottom-0 z-[100001] flex items-start justify-center overflow-hidden p-4 pb-8 font-sans print:inset-0 print:top-0 print:overflow-visible print:p-0">
                <div
                    className={`bg-pe-card dark:bg-[#111] rounded-2xl shadow-[0_0_40px_rgba(230,194,153,0.1)] border border-[#E6C299]/30 w-full ${modalWidthClass} max-h-[calc(100vh-9.5rem)] md:max-h-[calc(100vh-10rem)] flex min-h-0 flex-col justify-between overflow-hidden relative transition-all duration-200 my-2 print:shadow-none print:border-0 print:max-w-none print:max-h-none print:rounded-none print:my-0 print:bg-white`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute top-4 right-4 text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white bg-pe-elevated dark:bg-[#1a1a1a] hover:bg-pe-hover dark:hover:bg-[#222] rounded-lg p-2 transition-colors border border-pe-border-strong dark:border-[#333] z-[60] print:hidden"
                        aria-label="Schließen"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="bg-pe-elevated dark:bg-[#111622] z-50 shrink-0 print:hidden border-b border-pe-border dark:border-[#222]">
                        <div className="px-6 pt-10 pb-4">
                            <h3 className="text-lg font-bold text-pe-accent-text dark:text-[#E6C299] text-center uppercase tracking-widest pr-8 leading-snug">
                                50-€-Sachbezugsvereinbarung
                            </h3>
                            <p className="text-center text-xs text-pe-muted dark:text-slate-400 uppercase tracking-widest mt-2">
                                Schritt {currentStep} von 2: {STEP_LABELS[currentStep]}
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 custom-scrollbar print:p-0 print:overflow-visible">
                        {currentStep === 1 && (
                            <div className="space-y-4">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-1 border-b border-pe-border dark:border-[#222] pb-2">
                                    Arbeitgeber
                                </p>
                                {renderBetriebField('firmenname', 'Firma / Salonname')}
                                {renderBetriebField('strasse', 'Straße & Hausnummer')}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    {renderBetriebField('plz', 'PLZ')}
                                    <div className="sm:col-span-2">{renderBetriebField('ort', 'Ort')}</div>
                                </div>

                                <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mt-4 mb-1 border-b border-pe-border dark:border-[#222] pb-2">
                                    Arbeitnehmer
                                </p>
                                <div>
                                    <label className={LABEL_CLASS}>Aus Team wählen (optional)</label>
                                    <select
                                        value={selectedTeamEmployeeId}
                                        onChange={(e) => handleTeamEmployeeSelect(e.target.value)}
                                        className={INPUT_CLASS}
                                    >
                                        <option value="">— Manuell eingeben —</option>
                                        {teamEmployees.map((emp) => (
                                            <option key={emp.id} value={emp.id}>
                                                {emp.name} ({emp.pool})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className={LABEL_CLASS}>Name</label>
                                    <input
                                        type="text"
                                        value={mitarbeiterData.name}
                                        onChange={(e) => updateMitarbeiterField('name', e.target.value)}
                                        className={getInputClass(INPUT_CLASS, 'mitarbeiter_name')}
                                        autoComplete="name"
                                    />
                                    {renderFieldError('mitarbeiter_name')}
                                </div>
                                <div>
                                    <label className={LABEL_CLASS}>Straße &amp; Hausnummer</label>
                                    <input
                                        type="text"
                                        value={mitarbeiterData.strasse}
                                        onChange={(e) => updateMitarbeiterField('strasse', e.target.value)}
                                        className={getInputClass(INPUT_CLASS, 'mitarbeiter_strasse')}
                                        autoComplete="street-address"
                                    />
                                    {renderFieldError('mitarbeiter_strasse')}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div>
                                        <label className={LABEL_CLASS}>PLZ</label>
                                        <input
                                            type="text"
                                            value={mitarbeiterData.plz}
                                            onChange={(e) => updateMitarbeiterField('plz', e.target.value)}
                                            className={getInputClass(INPUT_CLASS, 'mitarbeiter_plz')}
                                            autoComplete="postal-code"
                                        />
                                        {renderFieldError('mitarbeiter_plz')}
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className={LABEL_CLASS}>Ort</label>
                                        <input
                                            type="text"
                                            value={mitarbeiterData.ort}
                                            onChange={(e) => updateMitarbeiterField('ort', e.target.value)}
                                            className={getInputClass(INPUT_CLASS, 'mitarbeiter_ort')}
                                            autoComplete="address-level2"
                                        />
                                        {renderFieldError('mitarbeiter_ort')}
                                    </div>
                                </div>

                                <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mt-4 mb-1 border-b border-pe-border dark:border-[#222] pb-2">
                                    Sachbezug
                                </p>
                                <div>
                                    <label className={LABEL_CLASS}>Art des Sachbezugs</label>
                                    <select
                                        value={sachbezugArt}
                                        onChange={(e) => setSachbezugArt(e.target.value as SachbezugArt)}
                                        className={INPUT_CLASS}
                                    >
                                        {SACHBEZUG_ART_OPTIONS.map((opt) => (
                                            <option key={opt} value={opt}>
                                                {opt}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className={LABEL_CLASS}>Details (z. B. Kartenanbieter)</label>
                                    <input
                                        type="text"
                                        value={sachbezugDetails}
                                        onChange={(e) => {
                                            setSachbezugDetails(e.target.value);
                                            clearFieldError('sachbezug_details');
                                        }}
                                        placeholder="z. B. Edenred City-Karte, Pluxee Tankkarte"
                                        className={getInputClass(INPUT_CLASS, 'sachbezug_details')}
                                    />
                                    {renderFieldError('sachbezug_details')}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className={LABEL_CLASS}>Monatlicher Wert (max. 50,00 €)</label>
                                        <input
                                            type="number"
                                            min={0.01}
                                            max={50}
                                            step={0.01}
                                            value={monatlicherWert}
                                            onChange={(e) => {
                                                setMonatlicherWert(e.target.value);
                                                clearFieldError('monatlicher_wert');
                                            }}
                                            onBlur={() => {
                                                const n = Number(String(monatlicherWert).replace(',', '.'));
                                                if (Number.isFinite(n)) setMonatlicherWert(String(clampWert(n)));
                                            }}
                                            className={getInputClass(INPUT_CLASS, 'monatlicher_wert')}
                                        />
                                        {renderFieldError('monatlicher_wert')}
                                    </div>
                                    <div>
                                        <label className={LABEL_CLASS}>Ausgabe-Tag (1–28)</label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={28}
                                            step={1}
                                            value={ausgabeTag}
                                            onChange={(e) => {
                                                setAusgabeTag(e.target.value);
                                                clearFieldError('ausgabe_tag');
                                            }}
                                            className={getInputClass(INPUT_CLASS, 'ausgabe_tag')}
                                        />
                                        {renderFieldError('ausgabe_tag')}
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className={LABEL_CLASS}>Ausgabe-Beginn</label>
                                        <input
                                            type="date"
                                            value={ausgabeBeginn}
                                            onChange={(e) => {
                                                setAusgabeBeginn(e.target.value);
                                                clearFieldError('ausgabe_beginn');
                                            }}
                                            className={getInputClass(DATE_INPUT_CLASS, 'ausgabe_beginn')}
                                        />
                                        {renderFieldError('ausgabe_beginn')}
                                    </div>
                                    <div>
                                        <label className={LABEL_CLASS}>Erstellungsdatum</label>
                                        <input
                                            type="date"
                                            value={erstellungsDatum}
                                            onChange={(e) => {
                                                setErstellungsDatum(e.target.value);
                                                clearFieldError('erstellungs_datum');
                                            }}
                                            className={getInputClass(DATE_INPUT_CLASS, 'erstellungs_datum')}
                                        />
                                        {renderFieldError('erstellungs_datum')}
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="w-full min-h-0 pr-2 mb-6">{renderContractDocument()}</div>
                        )}
                    </div>

                    {currentStep === 2 && (
                        <PePapersDisclaimerGate
                            accepted={disclaimerAccepted}
                            onAcceptedChange={setDisclaimerAccepted}
                        />
                    )}

                    {currentStep === 2 && (
                        <div className="px-6 pt-3 print:hidden">
                            <PePapersStorageHint
                                hint={PE_PAPERS_STORAGE_HINTS['sachbezug-50']}
                                variant="modal"
                            />
                        </div>
                    )}

                    <div className="px-6 py-4 border-t border-pe-border dark:border-[#222] shrink-0 print:hidden">
                        <div className="flex flex-wrap gap-3">
                            {currentStep > 1 && (
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(1)}
                                    className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors"
                                >
                                    Zurück
                                </button>
                            )}

                            {currentStep === 1 ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        {isSaving && archiveFeedback !== 'draft' ? 'Speichert...' : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleWeiter}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-[#E6C299] hover:bg-[#d9b183] dark:hover:bg-white text-black transition-colors"
                                    >
                                        Dokument erstellen
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving}
                                        className="flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        {archiveFeedback === 'draft' ? 'Gespeichert! ✓' : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(false)}
                                        disabled={isActionLocked || isSaving}
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-pe-card dark:bg-[#15171e] hover:bg-pe-hover dark:hover:bg-[#1a1d24] border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] transition-colors'
                                        )}
                                    >
                                        {isSaving && archiveFeedback !== 'draft'
                                            ? 'Speichert...'
                                            : archiveFeedback === 'success'
                                              ? 'Gespeichert! ✓'
                                              : 'Im Archiv speichern'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleDownloadPdf()}
                                        disabled={isActionLocked || isPdfDownloading}
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-gradient-to-b from-[#E6C299] to-[#b48a56] hover:from-[#f0d3b3] hover:to-[#cfa674] text-black transition-colors'
                                        )}
                                    >
                                        {isPdfDownloading ? 'PDF wird erstellt...' : 'Als PDF herunterladen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handlePrintIsolated()}
                                        disabled={isActionLocked || isPdfDownloading}
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] transition-colors'
                                        )}
                                    >
                                        Ausdrucken
                                    </button>
                                </>
                            )}
                        </div>
                        {currentStep === 2 && archiveFeedback === 'error' && (
                            <p className="text-[10px] text-red-400 mt-2 text-center">
                                Archivierung fehlgeschlagen. Bitte erneut versuchen.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default PeSachbezugModal;
